import React, {
  useEffect, useCallback, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { useDispatch } from 'react-redux';
import { Select, Option, TextDropdownSelect } from 'barnyard/components';
import { useDebounce } from 'barnyard/hooks';
import { manufacturersSearch } from '../actions';
import { useManufacturerSearchResults, useManufacturerSearchQuery } from '../selectors';


const ManufacturerSelect = (props) => {
  const dispatch = useDispatch();
  const {
    include_all,
    include_any,
    showTextSelect,
    onChange,
    ...other
  } = props;
  const [selected, setSelected] = useState({});
  const [initialValue] = useState(props.value);
  const [searchQuery, setSearchQuery] = useState('');
  const manufacturers = useManufacturerSearchResults({ manufacturer_ids: initialValue });
  const query = useManufacturerSearchQuery();

  // Debounce the search query
  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  // Trigger search when debounced query changes
  useEffect(() => {
    if (debouncedSearchQuery) {
      dispatch(manufacturersSearch({ q: debouncedSearchQuery }));
    }
  }, [debouncedSearchQuery, dispatch]);

  // Fetch initial manufacturer data on mount
  useEffect(() => {
    if (!_.isEmpty(initialValue)) {
      dispatch(manufacturersSearch({ manufacturer_ids: initialValue }));
    }
  }, [initialValue, dispatch]);

  const onChangeValue = useCallback((changeValue) => {
    const selectedValues = _.keyBy(
      _.filter(
        manufacturers,
        m => _.includes(changeValue, m.pk)
      ),
      'pk'
    );
    setSelected(currentValue => ({ ...currentValue, ...selectedValues }));
    onChange(changeValue);
  }, [manufacturers, onChange]);

  const unselectedManufacturers = useMemo(() => _.filter(
    manufacturers,
    m => !selected[m.pk]
  ), [manufacturers, selected]);

  let SelectComponent = Select;

  if (showTextSelect) {
    SelectComponent = TextDropdownSelect;
  }

  return (
    <SelectComponent
      {...other}
      onChange={onChangeValue}
      onSearch={setSearchQuery}
      filterOption={false}
      clearable={true}
      searchPlaceholder='Search Manufacturers...'
      notFoundContent={query ? 'No manufacturers found' : 'Type to search for a manufacturer'}
    >
      {include_all && <Option value='all'>All Manufacturers</Option>}
      {include_any && <Option value='any'>Any Manufacturer</Option>}
      {
        _.map(
          _.orderBy(selected, 'name'),
          manufacturer => (
            <Option value={manufacturer.pk} key={manufacturer.pk}>{manufacturer.name}</Option>
          )
        )
      }
      {_.map(unselectedManufacturers, manufacturer => (
        <Option value={manufacturer.pk} key={manufacturer.pk}>{manufacturer.name}</Option>
      ))}
    </SelectComponent>
  );
};

ManufacturerSelect.propTypes = {
  onChange: PropTypes.func.isRequired,
  include_all: PropTypes.bool,
  include_any: PropTypes.bool,
  manufacturersSearchAction: PropTypes.func.isRequired,
  showTextSelect: PropTypes.bool,
  value: PropTypes.arrayOf(PropTypes.number),
};

export default ManufacturerSelect;
